import React, { useEffect, useRef } from 'react';

const BAR_COUNT = 29;
const BASE_HEIGHT = 2;
const MAX_HEIGHT = 32;
const CENTER_Y = 20;

// This is a view of the recorder's stream, never a second microphone capture.
// In particular, no node is connected to the audio output.
export default function VoiceVisualizer({ stream, active = false }) {
  const bars = useRef([]);

  useEffect(() => {
    const setHeight = (index, height) => {
      const bar = bars.current[index];
      if (!bar) return;
      bar.setAttribute('height', String(height));
      bar.setAttribute('y', String(CENTER_Y - height / 2));
    };
    const reset = () => {
      for (let index = 0; index < BAR_COUNT; index += 1) setHeight(index, BASE_HEIGHT);
    };
    reset();
    if (!active || !stream?.getAudioTracks().some(track => track.readyState !== 'ended')) return undefined;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return undefined;

    let context;
    let source;
    let analyser;
    let animation;
    let disposed = false;
    const heights = Array(BAR_COUNT).fill(BASE_HEIGHT);
    const cleanup = () => {
      disposed = true;
      if (animation !== undefined) window.cancelAnimationFrame(animation);
      try { source?.disconnect(); } catch (_) { /* The stream may have ended already. */ }
      try { analyser?.disconnect(); } catch (_) { /* The context may have closed already. */ }
      if (context && context.state !== 'closed') {
        try { Promise.resolve(context.close()).catch(() => {}); } catch (_) { /* Optional visualization. */ }
      }
      reset();
      // The recording session owns the tracks; stopping them here would lose a take.
    };

    try {
      context = new AudioContext();
      analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      if (context.state === 'suspended') Promise.resolve(context.resume()).catch(() => {});

      const floats = typeof analyser.getFloatTimeDomainData === 'function';
      const samples = floats ? new Float32Array(analyser.fftSize) : new Uint8Array(analyser.fftSize);
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const interval = reducedMotion ? 120 : 32;
      let lastDraw = -Infinity;
      const draw = now => {
        if (disposed) return;
        if (now - lastDraw >= interval) {
          lastDraw = now;
          try {
            if (context.state === 'running') {
              if (floats) analyser.getFloatTimeDomainData(samples);
              else analyser.getByteTimeDomainData(samples);
              for (let index = 0; index < BAR_COUNT; index += 1) {
                const start = Math.floor(index * samples.length / BAR_COUNT);
                const end = Math.floor((index + 1) * samples.length / BAR_COUNT);
                let energy = 0;
                for (let sample = start; sample < end; sample += 1) {
                  const value = floats ? samples[sample] : (samples[sample] - 128) / 128;
                  energy += value * value;
                }
                const rms = Math.sqrt(energy / (end - start));
                const target = Math.min(MAX_HEIGHT, Math.max(BASE_HEIGHT, Math.sqrt(Math.max(0, rms - 0.008)) * 68));
                heights[index] = Math.round((heights[index] + (target - heights[index]) * 0.45) * 10) / 10;
                if (Math.abs(heights[index] - target) < 0.15) heights[index] = Math.round(target * 10) / 10;
                setHeight(index, heights[index]);
              }
            } else reset();
          } catch (_) {
            // An ended/unsupported stream must not interrupt the recording controls.
            reset();
          }
        }
        animation = window.requestAnimationFrame(draw);
      };
      animation = window.requestAnimationFrame(draw);
    } catch (_) {
      cleanup();
      return undefined;
    }
    return cleanup;
  }, [stream, active]);

  return <svg className="editor-recorder__visualizer" viewBox="0 0 240 40" width="100%" height="40"
    aria-hidden="true" focusable="false" data-active={active ? 'true' : 'false'}>
    {Array.from({ length: BAR_COUNT }, (_, index) => <rect key={index}
      ref={element => { bars.current[index] = element; }}
      x={6 + index * 8} y={CENTER_Y - BASE_HEIGHT / 2} width="4" height={BASE_HEIGHT} rx="2" fill="currentColor" />)}
  </svg>;
}
